import React, { useState, useContext, useMemo, useEffect } from 'react';
import {
  GridLegacy as Grid,
  Dialog,
  DialogTitle,
  DialogActions,
  Button,
  DialogContent,
} from '@mui/material';

import StagingDataTable from '../StagingDataTable';
import ColumnDefinition from '../../constants/ColumnDefinition';
import LocationTypeFilterPopover from '../filter/LocationTypeFilterPopover';
import StagingFilterBuilder, { FilterDefinition } from '../StagingFilterBuilder';
import {
  BooleanFilter,
  ContainsTextFilter,
  LocationTypeFilter,
} from '../../../../redux/constants/filters';
import FilterContext from '../../contexts/FilterContext';
import BooleanFilterOption from '../filter/BooleanFilterOption';
import TextFilterPopover from '../filter/TextFilterPopover';

export const LocationSelectorDialog = ({
  open,
  title,
  onSubmit,
  buildingId,
  loading,
  onClose,
  editing,
  locationsByName,
  defaultSelection,
}) => {
  const { filters, resetFilters } = useContext(FilterContext);
  const [selectedTargets, setSelectedTargets] = useState([]);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  const otherValidLocations = useMemo(
    () =>
      Object.values(locationsByName).filter(
        (l) =>
          l.name !== editing.name &&
          l.name !== editing.contained_by &&
          // undefined wyhen editing is equipment, otherwise ensure that floors aren't selected as children of non-floors
          (editing.type === undefined || editing.type === 'FLOOR' || l.type !== 'FLOOR')
      ),
    [locationsByName, editing]
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSelectedTargets(defaultSelection(editing));
    // Also tracking open to ensure proper population if equipment is closed and reopened
  }, [defaultSelection, editing, open]);

  const filterDefs = useMemo(
    () => [
      new FilterDefinition(
        BooleanFilterOption,
        'selected_only',
        'Only Selected',
        {},
        () =>
          new BooleanFilter({
            key: 'selected_only',
            getter: (e) => e.name,
            evaluation: (id) => selectedTargets.includes(id),
          })
      ),
      new FilterDefinition(
        TextFilterPopover,
        'name',
        'Location Name',
        {},
        (v) =>
          new ContainsTextFilter({
            key: 'name',
            getter: (e) => e.name,
            fragment: v,
          })
      ),
      new FilterDefinition(
        LocationTypeFilterPopover,
        'location_type',
        'Location Type',
        {},
        (type) =>
          new LocationTypeFilter({
            key: 'location_type',
            getter: (e) => e.type,
            type,
          })
      ),
    ],
    [selectedTargets]
  );

  const handleClose = (resetState) => {
    resetFilters();
    if (resetState) setSelectedTargets(defaultSelection(editing));
    onClose();
  };

  const handleSubmit = () => {
    setButtonsDisabled(true);
    onSubmit(selectedTargets).then((success) => {
      setButtonsDisabled(false);
      if (success) {
        handleClose(true);
      }
    });
  };

  const filteredTargets = useMemo(
    () => otherValidLocations.filter((e) => filters.every((f) => f.filter(e))),
    [otherValidLocations, filters]
  );

  const columns = [
    new ColumnDefinition({
      id: 'location_name',
      label: 'Location Name',
      type: 'text',
      hideable: true,
      render: (l) => l.name,
    }),
    new ColumnDefinition({
      id: 'location_type',
      label: 'Location Type',
      type: 'text',
      hideable: true,
      render: (l) => l.type,
    }),
  ];

  if (!open) return null;

  return (
    <Dialog open={open} maxWidth="lg" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Grid item xs={12}>
          <StagingFilterBuilder filterDefs={filterDefs} />
        </Grid>
        <Grid item xs={12}>
          <StagingDataTable
            columns={columns}
            allData={otherValidLocations}
            filteredData={filteredTargets}
            rowIdKey="name"
            loading={loading}
            buildingId={buildingId}
            selectedIds={selectedTargets}
            onItemSelected={(newPointTopics) => setSelectedTargets(newPointTopics)}
          />
        </Grid>
      </DialogContent>
      <DialogActions style={{ justifyContent: 'space-between' }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          color="success"
          disabled={buttonsDisabled}
        >
          Save
        </Button>
        <Button
          variant="contained"
          onClick={() => handleClose(true)}
          color="error"
          disabled={buttonsDisabled}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};
